import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Banknote, Coins, Gamepad2, Gift, RefreshCw, Users, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { moneyHint } from '@/lib/money';
import { formatDate } from '@/components/data-table/columns-helper';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { selfNodeId } from '@/lib/self-node';
import { signedMoney } from '@/pages/settlements/signed-money';

// ---------------------------------------------------------------------------
// Wire types. Money crosses the boundary as decimal strings of integer minor
// units and is never parsed into a JS number here.
// ---------------------------------------------------------------------------

interface BalanceSummaryRow {
  currency: string;
  walletCount: number;
  total: string;
  withdrawable: string;
  bonus: string;
  freegame: string;
}

interface BalanceSummary {
  asOf: string;
  items: BalanceSummaryRow[];
}

interface CreditSummary {
  /** `hasBook` is absent on a backend older than the field — see the read below. */
  self: { balance: string; currency: string; suspended: boolean; hasBook?: boolean } | null;
  /** Money only, one entry per currency. */
  downline: { currency: string; total: string }[];
  nodeCount: number;
  suspendedCount: number;
}

type NodeKind = 'root' | 'senior' | 'master' | 'agent';

/**
 * Whether this session's node is one that actually owns players.
 *
 * Players attach to an AGENT, so only an agent — or the platform/root view above
 * everything — has "player money of its own". A senior or master would otherwise
 * see its downline agents' player balances under a heading that reads as its
 * own, which is how the figure got mistaken for the master's money.
 *
 * This is presentation, not authorization: the caller's agent scope is unchanged
 * and every other backoffice page still shows the same downline rows, so the
 * endpoint stays open. Hiding the summary only stops it from being misread.
 *
 * An admin whose token carries no `tenancyNodeId` is a legacy/platform account —
 * treated as root, matching NodesTab. Anything unresolved fails closed.
 */
function useOwnsPlayers(enabled: boolean): { ready: boolean; ownsPlayers: boolean } {
  const token = useAuthStore((s) => s.accessToken);
  const nodeId = selfNodeId(token);

  const q = useQuery<{ kind: NodeKind }>({
    queryKey: ['/api/bo/organizations/me'],
    queryFn: () => api.get<{ kind: NodeKind }>('/api/bo/organizations/me'),
    // Skipped when the caller could not see the group anyway — no point asking
    // who they are to decide whether to hide something already hidden.
    enabled: enabled && nodeId !== null,
    retry: false,
    staleTime: Infinity,
  });

  if (!enabled) return { ready: true, ownsPlayers: false };
  if (nodeId === null) return { ready: true, ownsPlayers: true };
  if (q.isError) return { ready: true, ownsPlayers: false };
  if (!q.data) return { ready: false, ownsPlayers: false };
  return { ready: true, ownsPlayers: q.data.kind === 'agent' || q.data.kind === 'root' };
}

/**
 * `moneyHint` strips every non-digit before formatting, so a negative minor-unit
 * string would silently render as its own magnitude. Peel the sign off first and
 * re-apply it, and report it so the caller can colour the figure.
 */
function balanceText(minor: string, currency: string): { text: string; negative: boolean } {
  const raw = String(minor ?? '').trim();
  const negative = raw.startsWith('-');
  const magnitude = negative ? raw.slice(1) : raw;
  const hint = moneyHint(magnitude, currency) || moneyHint('0', currency) || '-';
  return { text: negative ? `−${hint}` : hint, negative };
}

/**
 * Everything the operator is holding right now, in one section.
 *
 * Player wallets and agent prepaid credit are different ledgers with different
 * refresh characteristics, but they answer the same question — "how much money
 * is sitting in the system" — so they read as one section split into two
 * labelled groups rather than as two unrelated cards stacked on top of the
 * dashboard. The group labels carry the distinction that the numbers alone
 * cannot: a player balance is a liability to players, node credit is not.
 *
 * The two halves keep their own permission gate and their own query. Either can
 * be absent; the section disappears entirely only when both are.
 */
export function DashboardBalancesSection() {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canReadWallet = hasPermission('wallet:read');
  const canReadCredit = hasPermission('mlm:credit_read');

  const creditQ = useQuery<CreditSummary>({
    queryKey: ['/api/bo/mlm-credit/summary'],
    queryFn: () => api.get<CreditSummary>('/api/bo/mlm-credit/summary'),
    enabled: canReadCredit,
    retry: false,
    // Stop dead on error rather than spraying a failed request every 30s.
    refetchInterval: (query) => (query.state.error ? false : 30_000),
    refetchOnWindowFocus: (query) => !query.state.error,
  });

  const { ready, ownsPlayers } = useOwnsPlayers(canReadWallet);

  const showCredit = canReadCredit && !creditQ.isError && !!creditQ.data;
  const showPlayers = ready && ownsPlayers;
  if (!showPlayers && !showCredit) return null;

  return (
    <section className="space-y-3" data-testid="dashboard-balances">
      <h3 className="text-sm font-semibold text-muted-foreground">
        {t('dashboard.balances.sectionTitle')}
      </h3>

      {showPlayers ? <PlayerMoneyGroup /> : null}
      {showCredit ? <AgentCreditGroup summary={creditQ.data!} /> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Player money
// ---------------------------------------------------------------------------

/**
 * Total player money held, per currency.
 *
 * Deliberately NOT polled: unlike the credit group below, each read scans every
 * wallet row in scope, so it loads once per dashboard visit and refreshes only
 * when an operator asks. `asOf` is stamped server-side and always shown — a live
 * figure with no timestamp invites reading a stale render as current.
 */
function PlayerMoneyGroup() {
  const { t, i18n } = useTranslation();

  const q = useQuery<BalanceSummary>({
    queryKey: ['/api/bo/wallets/summary'],
    queryFn: () => api.get<BalanceSummary>('/api/bo/wallets/summary'),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const rows = q.data?.items ?? [];

  return (
    <div className="space-y-2" data-testid="dashboard-total-balance">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          {t('dashboard.balances.playersGroup')}
        </span>
        <div className="flex items-center gap-2">
          {q.data ? (
            <span className="text-[11px] text-muted-foreground">
              {t('dashboard.balances.asOf', {
                time: formatDate(q.data.asOf, { locale: i18n.language, withTime: true }),
              })}
            </span>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => void q.refetch()}
            disabled={q.isFetching}
            aria-label={t('dashboard.balances.refresh')}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', q.isFetching && 'animate-spin')} />
            {t('dashboard.balances.refresh')}
          </Button>
        </div>
      </div>

      {q.isError ? (
        <Card className="p-3 text-sm text-destructive">{t('dashboard.balances.error')}</Card>
      ) : rows.length === 0 ? (
        <Card className="p-3 text-sm text-muted-foreground">
          {q.isLoading ? '—' : t('common.noData')}
        </Card>
      ) : (
        rows.map((row) => (
          <CurrencyRow key={row.currency} row={row} showCurrency={rows.length > 1} />
        ))
      )}
    </div>
  );
}

/**
 * One 4-up card row per currency. Totals are kept apart per currency and never
 * added together; freegame sits outside `total` for the same reason the
 * player-facing balance excludes it — it is provider free-round credit, not
 * money the player holds.
 */
function CurrencyRow({ row, showCurrency }: { row: BalanceSummaryRow; showCurrency: boolean }) {
  const { t } = useTranslation();
  const total = balanceText(row.total, row.currency);

  return (
    <div className="space-y-1.5">
      {showCurrency ? (
        <div className="text-[11px] font-medium text-muted-foreground">{row.currency}</div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          icon={<Wallet className="h-4 w-4 text-sky-500" />}
          label={t('dashboard.balances.playerTotal')}
          value={total.text}
          negative={total.negative}
          hint={t('dashboard.balances.wallets', { count: row.walletCount })}
        />
        <MoneyStatCard
          icon={<Banknote className="h-4 w-4 text-emerald-500" />}
          label={t('dashboard.balances.withdrawable')}
          minor={row.withdrawable}
          currency={row.currency}
        />
        <MoneyStatCard
          icon={<Gift className="h-4 w-4 text-pink-500" />}
          label={t('dashboard.balances.bonus')}
          minor={row.bonus}
          currency={row.currency}
        />
        <MoneyStatCard
          icon={<Gamepad2 className="h-4 w-4 text-amber-500" />}
          label={t('dashboard.balances.freegame')}
          minor={row.freegame}
          currency={row.currency}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent prepaid credit
// ---------------------------------------------------------------------------

/**
 * Prepaid credit for the logged-in node and everything beneath it.
 *
 * A structural/root caller has no credit book, so `self` comes back null and only
 * the downline card renders. An empty downline shows its node count without a
 * money figure — there is no currency to denominate a zero in when `self` is null.
 *
 * The node counts come from the server as top-level fields and are used as-is: they
 * count DISTINCT nodes, while `downline` is keyed by currency and one node may hold
 * a book in several. Re-deriving a count by summing the currency entries would
 * report a dual-currency agent twice.
 */
function AgentCreditGroup({ summary }: { summary: CreditSummary }) {
  const { t } = useTranslation();
  const { self, downline, nodeCount, suspendedCount } = summary;
  // A node that never got a book reports balance '0' like a node that spent one to
  // zero; only `hasBook` separates them, and the two states read very differently.
  // Strictly `=== false`: this SPA deploys independently of svc-commission, so a
  // response predating the field must degrade to showing money, not hide every
  // funded agent's balance behind the no-book caption fleet-wide.
  const selfMoney = self && self.hasBook !== false ? signedMoney(self.balance, self.currency) : null;
  const selfAlert = !!self && !!selfMoney && (self.suspended || selfMoney.negative);

  return (
    <div className="space-y-2">
      <span className="text-xs font-medium text-muted-foreground">
        {t('dashboard.balances.creditGroup')}
      </span>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {self ? (
          <Card className="p-3" data-testid="dashboard-credit-self">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Coins className="h-4 w-4 text-violet-500" />
              <span className="truncate">{t('dashboard.credit.self')}</span>
            </div>
            {selfMoney ? (
              <>
                <div
                  className={cn(
                    'mt-1 text-lg font-semibold tabular-nums',
                    selfAlert && 'text-destructive',
                  )}
                >
                  {selfMoney.text}
                </div>
                <div
                  className={cn(
                    'mt-0.5 text-[11px]',
                    selfAlert ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  {selfAlert ? t('dashboard.credit.outOfCredit') : t('dashboard.credit.ready')}
                </div>
              </>
            ) : (
              <div className="mt-1 text-sm text-muted-foreground">{t('mlm.credit.noBook')}</div>
            )}
          </Card>
        ) : null}

        <Card className="p-3 transition-colors hover:bg-accent/50" data-testid="dashboard-credit-downline">
          <Link to="/organizations" className="block">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Users className="h-4 w-4 text-sky-500" />
              <span className="truncate">{t('dashboard.credit.downline')}</span>
            </div>
            <div className="mt-1 space-y-0.5">
              {downline.map((r) => (
                <div key={r.currency} className="text-lg font-semibold tabular-nums">
                  {signedMoney(r.total, r.currency).text}
                </div>
              ))}
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              <span>{t('dashboard.credit.nodes', { count: nodeCount })}</span>
              {suspendedCount > 0 ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="text-destructive">
                    {t('dashboard.credit.suspendedNodes', { count: suspendedCount })}
                  </span>
                </>
              ) : null}
            </div>
          </Link>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card primitives — same p-3 / text-lg shape as the dashboard's KpiCard so the
// section sits in the page's existing rhythm.
// ---------------------------------------------------------------------------

function StatCard({
  icon,
  label,
  value,
  negative,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  negative?: boolean;
  hint?: string;
}) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className={cn('mt-1 text-lg font-semibold tabular-nums', negative && 'text-destructive')}>
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div> : null}
    </Card>
  );
}

function MoneyStatCard({
  icon,
  label,
  minor,
  currency,
}: {
  icon: React.ReactNode;
  label: string;
  minor: string;
  currency: string;
}) {
  const value = balanceText(minor, currency);
  return <StatCard icon={icon} label={label} value={value.text} negative={value.negative} />;
}
